import { useApolloClient } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Label, Select, Spinner } from '@treams/treams-ui';

import BulkModal from './BulkModal';

export default function BulkRemoveCycleModal(props: {
	users: graphql.User[];
	onDismiss: () => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const client = useApolloClient();

	const cycles = graphql.useCycleTemplatesQuery({
		variables: {
			isArchived: false,
			first: 100,
		},
	});

	const [cycleTemplateID, setCycleTemplateID] = useState<string | undefined>();

	const [removeCycleInstance] = graphql.useRemoveCycleInstanceMutation();

	const options = cycles.data?.cycleTemplates.edges.map(({ node }) => ({
		title: localize(node.title).text,
		value: node.id,
	}));
	options?.sort((a, b) => a.title.localeCompare(b.title));

	return (
		<BulkModal
			name="bulkRemoveCycle"
			valid
			apply={async (user) => {
				const response = (await client.query({
					query: graphql.UserCyclesDocument,
					variables: { id: user.id },
				})) as graphql.UserCyclesQueryResult;

				let deleted = 0;

				for (const { node: cycle } of response.data?.user.cycles.edges ?? []) {
					if (cycle.cycleTemplate.id !== cycleTemplateID) {
						continue;
					}

					await removeCycleInstance({ variables: { input: { id: cycle.id } } });

					deleted += 1;
				}

				return deleted;
			}}
			users={props.users}
			onDismiss={props.onDismiss}
		>
			<Label mb={2}>{t('employees.bulkRemoveCycle.cycle.label')}</Label>
			{cycles.data ? (
				<Flex>
					<Box width={1 / 2}>
						<Select
							selectedValue={cycleTemplateID}
							options={options}
							onChange={(option) => setCycleTemplateID(option)}
							placeholder={t('common.select.placeholder')}
						/>
					</Box>
				</Flex>
			) : (
				<Spinner centered />
			)}
		</BulkModal>
	);
}
